import type { EventItem } from "../indices/events/event-item.ts";
import { EventPatch } from "../indices/events/event-patch.ts";
import type { SheetsNetwork } from "../wire/rows-network.ts";
import type { Reduce } from "./overlay.ts";
import type { ConfirmResult, ToConfirmResult } from "./pending-mutation.ts";
import { AuthId, CalendarId, type EventId } from "../ids/ids.ts";

/**
 * The events binding of the generic mutation overlay substrate — the semantic op
 * union + the reducer + the confirm-fold + the wire-patch application. It lives in
 * `@tento/lona/sheets` (the lower data-model layer) alongside the core `EventItem`
 * it operates on, so the sheets-side `EventsFacade` can own a per-row
 * `Overlay<EventItem, EventMutationOp, EventId>` and derive its read surface from it. The
 * generic core (`overlay.ts` / `mutation-backend.ts`) knows nothing about events;
 * the cloud-HTTP half (`eventBodyFor` / `EventCloudMutationBackend`) stays up in
 * `@tento/lona` (it needs `SheetsNetwork.Routes` + `TypedApi`) and is injected down.
 *
 * An update op carries the write's wire body — what `EventPatch.encode`
 * produced for it, the body the request sends — and the reducer paints it
 * with `EventPatch.paintWire`, so the overlay shows exactly what the server
 * applies.
 */
export type EventMutationOp =
  | { readonly kind: "insert-event"; readonly item: EventItem }
  | {
      readonly kind: "update-event";
      readonly id: EventId;
      readonly patch: SheetsNetwork.EventUpdate;
      readonly authId: AuthId;
      readonly calendarId: CalendarId;
    }
  | { readonly kind: "delete-event"; readonly id: EventId };

/** Apply an event op to the snapshot. Update paints the op's wire body
 *  (`EventPatch.paintWire`); insert/delete set/remove by id. */
export const eventReduce: Reduce<EventItem, EventMutationOp, EventId> = (snap, op) => {
  switch (op.kind) {
    case "insert-event":
      snap.set(op.item.id, op.item);
      return;
    case "update-event": {
      const cur = snap.get(op.id);
      if (cur) {
        snap.set(op.id, EventPatch.paintWire(cur, op.patch).updated);
      }
      return;
    }
    case "delete-event":
      snap.delete(op.id);
      return;
  }
};

/** Resolve a submitted event op + its ack into the overlay's fold form. Insert /
 *  update fold the server-materialized canonical event under its real id (the
 *  optimistic id is replaced); delete folds by the op's id (the ack carries no
 *  item). The op-aware delete id is exactly why this lives in the binding, not the
 *  generic overlay. */
export const eventConfirmResult: ToConfirmResult<EventMutationOp, EventItem, EventId> = (
  op,
  ack,
): ConfirmResult<EventItem, EventId> => {
  if (op.kind === "delete-event") return { kind: "delete", id: op.id };
  const item = ack.canonical;
  if (item == null) {
    throw new Error(
      `eventConfirmResult: ${op.kind} ack missing the canonical event`,
    );
  }
  return { kind: "upsert", id: item.id, item };
};
